import {AsyncNotice} from "../notices/notice";
import UserMarks from "../books/user-marks";
import {Spinner} from "../spinner";
import {appColors, insertOverlayOnElement, removeOverlayOnElement} from "../utilities/general";
import {
  findMarkInSectionCollectionInSessionStorage,
  getSectionMarksFromSessionStorage,
  updateMarkInSectionCollectionInSessionStorage
} from './session-storage';
import {
  findTagOnPageByText,
  manageTextCountElement,
  isTagManagementOnReader,
  sortTagsByNameSortingFunction,
  spotlightTag,
  removeSpotlightFromTags,
  tagsSortingFunction,
  isParentVisible
} from "../account/tags/functions";

//# sourceMappingURL=manage-tags.js.map

/**
 * Render the tags management controls in the DOM.
 *
 * @param event
 * @param {Event} event
 * @param {string} targetPage
 */
async function createTagManagementControls(event, targetPage) {
  /** main tags controls div **/
  const optionsDiv = event.target.closest('.annotation-options');

  const {dataset: {markId = null}} = optionsDiv;
  // guard clause - return if we do not have a mark id to attach to
  if (!markId) {
    let msg = 'There was a problem loading the tag management controls for this annotation.';
    let notice = new AsyncNotice(msg, 'error');
    await notice.create({}, true);
    notice.remove();

    return null;
  }

  let manageTagsArea, inputTextElem, suggestedTagsWrapper, existingTagsWrapper, p;

  /** main div container for the tags management controls **/
  const controlsDiv = document.createElement('div');
  controlsDiv.setAttribute('data-id', markId);
  controlsDiv.classList.add('annotation-options');
  controlsDiv.classList.add('no-actions-top');
  controlsDiv.setAttribute('data-controls-for', 'tags');
  controlsDiv.setAttribute('data-initial-open', 'true');

  // 'x' button to close the options div
  const a = document.createElement('a');
  a.classList.add('close-button');

  let i = document.createElement('i');
  i.classList.value = 'fas fa-times-circle';
  i.setAttribute('aria-hidden', 'true');
  a.appendChild(i);

  // event to hide the options div
  a.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    controlsDiv.classList.add('hide');
  });

  controlsDiv.append(a);

  // 1st element - existing tags wrapper - markData needed: id, tags (ids)
  let marks, markData, userMarkRecordId;
  if (targetPage === 'reader') {
    marks = getSectionMarksFromSessionStorage();
    markData = marks.find((element) => parseInt(element.id) === parseInt(markId));
    markData.tags = markData.tags ? markData.tags : [];
  } else {
    // targetPage === 'annotations'
    let tags = [];
    const annotationWrapper = event.target.closest('[data-annotation]');
    if (annotationWrapper) {
      const { dataset: { markId, userMarkRecordId: annotationMarkRecordId = null } } = annotationWrapper;
      userMarkRecordId = annotationMarkRecordId;

      const markTagsElements = annotationWrapper.querySelectorAll('.tags-wrapper .tag');
      if(markTagsElements) {
        markTagsElements.forEach((tag) => {
          let { dataset: { id, color, usage, lastUpdated, text } } = tag;
          tags.push({color, id, lastUpdated, text, usage});
        })
      }

      markData = {'id': markId, tags};
    }
  }

  // assigned tags label
  p = document.createElement('p');
  p.classList.add('title');
  p.setAttribute('data-label-type', 'assigned');
  p.textContent = 'Assigned Tags';

  // container with existing tags
  const showUsage = true;
  existingTagsWrapper = renderExistingTags(markData, 'tags', showUsage);
  if (markData.tags.length > 4) {
    createDeleteAllButton(existingTagsWrapper, markData);
  }

  // wrapper for existing tags area
  const existingTagsArea = document.createElement('div');
  existingTagsArea.classList.add('add-edit-tag-area');

  existingTagsArea.appendChild(p);
  existingTagsArea.appendChild(existingTagsWrapper);
  controlsDiv.append(existingTagsArea);

  /** tags management area **/
  manageTagsArea = document.createElement('div');
  manageTagsArea.classList.add('add-edit-tag-area');

  // 1st element - input field
  inputTextElem = document.createElement('input');
  inputTextElem.setAttribute('type', 'text');
  inputTextElem.setAttribute('data-id', 'tag-input');
  inputTextElem.setAttribute('placeholder', 'Add a tag');
  inputTextElem.focus();

  /* event handler - text input keyup */
  inputTextElem.addEventListener('keyup', async (event) => {
    event.preventDefault();

    // update the text count
    const textCountSpan = document.querySelector('.text-count span');
    const noticeDiv = document.querySelector('[data-notice]');
    const noticeElement = noticeDiv?.querySelector('.tm-notice');
    const { target: { value: text } } = event;

    if(text.length > 50) {
      event.target.value = text.slice(0, 50);
    } else {
      manageTextCountElement(text, textCountSpan);
      // 'Enter' key handled below
      if (event.key !== 'Enter') {
        noticeElement.textContent = '';
        // const notice = document.querySelector('.tm-notice');
        // if(notice && notice.textContent === 'Tag already exists') {
        //   return;
        // }
        const tagManagementDiv = findTagManagementDivForMark(markId);
        const sortElement = tagManagementDiv.querySelector(`.sorted[data-sort-by]`)
        let {dataset: {sortBy}} = sortElement;
        updateTagManagementElements(markId, sortBy, 'asc', userMarkRecordId);
      }
    }
  })

  // 2nd element -  Text count
  const textSpan = document.createElement('span');
  textSpan.innerText = '0';

  const textCount = document.createElement('div');
  textCount.classList.add('text-count');
  textCount.appendChild(textSpan);

  const denominator = document.createTextNode('/50');
  textCount.appendChild(denominator);

  // 3rd element - new tag wrapper
  const newTagWrapper = document.createElement('div');
  const tagData = {color: 'default', id: null, lastUpdated: null, text: '',  usage: '0'};

  // new tag div
  const newTagDiv = createNewTagDiv(markId, 'new', tagData);
  newTagWrapper.classList.add('new-tag');
  newTagWrapper.appendChild(newTagDiv);

  const noticeWrapper = document.createElement('div');
  noticeWrapper.setAttribute('data-notice', '');
  noticeWrapper.classList.add('hide');

  const noticeElement = document.createElement('div');
  noticeElement.classList.add('tm-notice');
  noticeWrapper.appendChild(noticeElement);
  newTagWrapper.appendChild(noticeWrapper);

  // 4th element - suggestions area
  suggestedTagsWrapper = document.createElement('div');
  suggestedTagsWrapper.classList.add('suggested-tags-wrapper');

  // color controls
  const colorControlsElem = createColorControls(markId);

  // 1st element - label & sort controls
  // Sort row Title
  let sortTitleDiv = document.createElement('div');
  sortTitleDiv.classList.add('auto-cell');

  p = document.createElement('p');
  p.classList.add('title');
  p.textContent = 'Suggestions';

  sortTitleDiv.appendChild(p);

  let sortOptionsDiv = document.createElement('div');
  sortOptionsDiv.classList.add('auto', 'cell', 'sort-options');

  // Sort A-Z icon
  let span = document.createElement('span');
  span.setAttribute('title', 'Sort A-Z');
  span.setAttribute('data-sort-by', 'name');
  span.classList.add('sorted');

  i = document.createElement('i');
  i.classList.add('fa-solid', 'fa-arrow-down-a-z');

  span.addEventListener('click', (event) => {
    event.preventDefault();

    console.log('sort alphabetically');
    sortSuggestedTags(event.target, 'name');
  });

  span.appendChild(i);
  sortOptionsDiv.appendChild(span);

  // Sort most recent icon
  span = document.createElement('span');
  span.setAttribute('title', 'Sort by Most Recent');
  span.setAttribute('data-sort-by', 'time');

  i = document.createElement('i');
  i.classList.add('fa-solid', 'fa-clock');

  span.addEventListener('click', (event) => {
    event.preventDefault();

    console.log('sort by time');
    sortSuggestedTags(event.target, 'time', 'desc');
  });

  span.appendChild(i);
  sortOptionsDiv.appendChild(span);

  // Sort most used
  span = document.createElement('span');
  span.setAttribute('title', 'Sort by Most Used');
  span.setAttribute('data-sort-by', 'usage');

  i = document.createElement('i');
  i.classList.add('fa-solid', 'fa-chart-line');

  span.addEventListener('click', (event) => {
    event.preventDefault();

    console.log('sort by most used');
    sortSuggestedTags(event.target, 'usage', 'desc');
  });

  span.appendChild(i);
  sortOptionsDiv.appendChild(span);

  // 2nd element - suggested tags div
  const suggestedTagsDiv = document.createElement('div');
  suggestedTagsDiv.classList.add('suggested-tags');

  // initial no contents span
  const noneSpan = document.createElement('span');
  noneSpan.innerHTML = 'No suggestions.';

  suggestedTagsDiv.appendChild(noneSpan);

  // Suggested tags title and Sort options
  let sortRowDiv = document.createElement('div');
  sortRowDiv.classList.add('grid-x');
  sortRowDiv.appendChild(sortTitleDiv);
  sortRowDiv.appendChild(sortOptionsDiv);

  suggestedTagsWrapper.appendChild(sortRowDiv);
  suggestedTagsWrapper.appendChild(suggestedTagsDiv);

  // add the suggested tags wrapper to the main manage tags div
  manageTagsArea.appendChild(inputTextElem);
  manageTagsArea.appendChild(textCount);
  manageTagsArea.appendChild(newTagWrapper);
  manageTagsArea.appendChild(colorControlsElem);
  manageTagsArea.appendChild(suggestedTagsWrapper);

  // manage tags area to main controls div
  controlsDiv.appendChild(manageTagsArea);

  /*** handle 'Enter' press ***/
  const boundKeyUpListener = (event) => { keyUpListener(event, inputTextElem, markId)};
  document.addEventListener('keyup', boundKeyUpListener);

  return controlsDiv;
}

/**
 * Listener to key events; handles 'Enter', 'ArrowLeft', 'ArrowRight'
 * @param event
 * @param inputTextElem
 * @param markId
 */
async function keyUpListener(event, inputTextElem, markId) {
  const noticeDiv = document.querySelector('[data-notice]');
  const noticeElement = noticeDiv?.querySelector('.tm-notice');
  const markElem = getMarkElement(markId);
  const newTagColorsDiv = markElem.querySelector('[data-color-popup]');
  const newTagColorsWrapper = newTagColorsDiv?.querySelector('.tag-colors-wrapper');
  const selectedColor = newTagColorsDiv?.querySelector('.selected');

  if(!newTagColorsDiv) return;

  if (event.key === 'Escape') {
    if (!newTagColorsDiv.classList.contains('hide')) {
      selectedColor.classList.remove('selected');
      const defaultColorElem = newTagColorsDiv.querySelector('.tag.default');
      defaultColorElem.classList.add('selected');
      newTagColorsDiv.classList.add('hide');
      inputTextElem.focus();
    }
  }

  if (event.key === 'Enter') {
    if (document.activeElement === inputTextElem) {
      const inputText = inputTextElem.value.trim();

      if (inputText.length === 0) {
        noticeElement.textContent = 'No text entered.';
        noticeElement.classList.remove('hide');
        noticeDiv.classList.remove('hide');
        return;
      }

      // fetch fresh existing tags every time 'Enter' is pressed
      const existingTags = markElem.querySelectorAll('.existing-tags .tag');
      const alreadyAssigned = Array.from(existingTags).find(tag =>
          tag.dataset.tagText?.toLowerCase() === inputText.toLowerCase()
      );

      if (alreadyAssigned) {
        noticeElement.textContent = 'Tag already assigned.';
        noticeElement.classList.remove('hide');
        noticeDiv.classList.remove('hide');
        return;
      }

      // Check suggested tags
      const suggestions = markElem.querySelectorAll('.suggested-tags .tag');
      const match = Array.from(suggestions).find(tag =>
          tag.dataset.tagText?.toLowerCase() === inputText.toLowerCase()
      );

      if (match) {
        match.click();
        return;
      }

      // No match found → show color picker
      newTagColorsDiv.classList.remove('hide');
      if (!newTagColorsWrapper.hasAttribute('tabindex')) {
        newTagColorsWrapper.setAttribute('tabindex', '0');
      }
      newTagColorsWrapper.focus();
    } else if (isTagColorSelectorForMarkVisible(markId)) {//&& newTagColorsWrapper.contains(document.activeElement)
      await handleTagColorSelectionEnter(event, markId);
    }
  }

  if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
    tagColorSelectorArrowEventHandler(event, markId);
  }
}
/**
 * Listener for arrow keys when the tags color palette is visible
 */
function tagColorSelectorArrowEventHandler(event, markId) {
  // 'Enter' handled elsewhere
  if (event.key === 'Enter') {
    return;
  }

    // Listen for keyup
    const colorSelectorWrapper = document.querySelector(`.tag-colors-wrapper[data-mark-id="${markId}"]`)
    const colorSelector = colorSelectorWrapper.closest('.color-selector');
    const {children} = colorSelectorWrapper;

    // guard clauses
    if (!isParentVisible(colorSelector)) return;
    // if (!colorSelectorWrapper.contains(document.activeElement)) return;

    // get the current index of teh selected child before handling key
    let selectedIndex
        = Array
        .from(children)
        .findIndex(child => child.classList.contains('selected'));

    // fallback to 0 if none are selected
    if (selectedIndex === -1) selectedIndex = 0;

    let newColor = 'default';
    if (event.key === 'ArrowLeft') {
      selectedIndex = (selectedIndex - 1 + children.length) % children.length; // wrap around
      newColor = updateSelectedColorOnTagColorPicker(colorSelectorWrapper, selectedIndex);
    } else if (event.key === 'ArrowRight') {
      selectedIndex = (selectedIndex + 1) % children.length; // wrap around
      newColor = updateSelectedColorOnTagColorPicker(colorSelectorWrapper, selectedIndex);
    }

    // update the new tag element color
    const newTagDiv = document.querySelector(_newTagElementSelector(markId));

    if(newTagDiv) {
      changeNewTagDivColor(newColor, newTagDiv);
    }
}

/**
 * Move the selector bar to the color that is currently selected.
 * @param colorSelector
 * @param index
 */
function updateSelectedColorOnTagColorPicker (colorSelector, index) {
  let color = 'default';

  const colorElement = colorSelector.querySelectorAll('.tag');
  colorElement.forEach((elem, i) => {
    if (i === index) {
      elem.classList.add('selected');
      elem.focus();
      color = elem.dataset.tagColor;
    } else {
      elem.classList.remove('selected');
    }
  });

  return color;
}

/**
 * Event handler for 'Enter' key on tag color selection
 * @param event
 * @param markId
 *
 * @returns {Promise<void>}
 */
async function handleTagColorSelectionEnter(event, markId) {
  const tagManagementDiv = findTagManagementDivForMark(markId);
  await addNewTagHandler(event, tagManagementDiv, 'new');
}

/**
 * Check if the tag color selector for a given mark is visible.
 * @returns {boolean}
 */
function isTagColorSelectorForMarkVisible(markId) {
  const tagsColorWrapper = document.querySelector(`.tag-colors-wrapper[data-mark-id="${markId}"]`);

  if(!tagsColorWrapper) {
    return false;
  }

  const colorSelector = tagsColorWrapper.closest('.color-selector');
  if(!colorSelector) {
    return false;
  }

  const { classList } = colorSelector;

  return !classList.contains('hide') && colorSelector.parentElement !== null;
}

/**
 * Sort the suggested tags by the option selected.
 * The sortSelectionElement is the sort button clicked on by teh user
 * set the clicked on sort button as active.
 * @param sortSelectionElement
 * @param sortBy - one of: ('name', 'time', 'usage')
 * @param direction - one nof: ('asc', 'desc')
 */
function sortSuggestedTags(sortSelectionElement, sortBy, direction) {
  const mark = sortSelectionElement.closest('mark');
  const markId = mark?.dataset.id;

  // guard clause
  if(markId === null || markId === undefined) {
    return;
  }

  // remove the 'sorted' class from all the sort buttons
  const sortOptionsWrapper = sortSelectionElement.closest('.sort-options');
  const sortOptionsElements = sortOptionsWrapper.querySelectorAll('[data-sort-by]');
  if(sortOptionsElements) {
    sortOptionsElements.forEach(element => {
      element.classList.remove('sorted');
    })
  }

  // set the target span button as sorted
  if(sortSelectionElement.nodeName.toLowerCase() === 'i') {
   sortSelectionElement = sortSelectionElement.closest('[data-sort-by]');
  }

  sortSelectionElement.classList.add('sorted');

  // clear the suggested tags area
  resetSuggestedTags(markId);

  // sort the tags
  updateTagManagementElements(markId, sortBy, direction);
}

/**
 * Find a mark in the session storage array, by mark id.
 *
 * @param markId
 * @returns {*}
 */
function findMarkInSession(markId) {
  let marks = JSON.parse(sessionStorage.getItem('marks'));

  return  findMarkInSectionCollectionInSessionStorage(marks, markId);
}

/**
 * Remove a tag from a mark's variables in session storage.
 * The mark record in 'marks' should be updated.
 *
 * @param markData
 * @param tagText
 */
function removeTagFromMarkInSessionStorage(markData, tagText) {
  const allMarks = getSectionMarksFromSessionStorage();
  for(let [_index, tag] of markData.tags.entries()) {
    if (tagText === tag.text) {
      markData.tags = removeFirstOccurrenceFromArray(markData.tags, tag);
      updateMarkInSectionCollectionInSessionStorage(allMarks, markData);
    }
  }
}

/**
 * Handler for removing a tag from a mark:
 * - Update session variables
 * - remove the tag element from the tags area
 * - reset the controls to create a new tag
 * - re-renders suggested tags.
 *
 * @param event
 * @param markId
 */
async function removeTagHandler(event, markId) {
  event.preventDefault();

  const tagsManagementElement = findTagManagementDivForMark(markId);
  const overlayId = `overlay-${markId}`;

  if(tagsManagementElement) {
    insertOverlayOnElement(tagsManagementElement, overlayId);
  }

  const { target, target: { nodeName, parentNode, dataset: { tagText } } } = event;
  const deletingAll = tagText === 'Delete All';

  // guard clause
  if (markId === null || markId === undefined) {
    return;
  }

  const assignedTagsElem = findExistingTagsDivForMark(markId);
  const allMarks = getSectionMarksFromSessionStorage();

  let tagElement = target;
  let span, text;

  if (nodeName === 'SPAN') {
    text = tagText;
    tagElement = parentNode;
  } else if (nodeName === 'I') {
    span = target.parentElement.querySelector('span'); text = span.dataset.tagText;
    tagElement = parentNode;
  } else {
    span = tagElement.querySelector('span');
    text = span.dataset.tagText;
  }

  // save mark state in case we have to revert
  const currentMarkSessionState = findMarkInSession(markId);

  // hide tags until result of delete
  if (deletingAll) {
    const markData = findMarkInSession(markId);
    if (assignedTagsElem) {
      assignedTagsElem
          .childNodes
          .forEach(
            function(tag) {
              const { dataset: { tagText: text, tagType } } = tag;
              tag.classList.add('hide');
              if (tagType !== 'delete') {
                let tagText = text;
                if(!tagText) {
                  tagText = tag.querySelector('span')?.dataset.tagText;
                }
                removeTagFromMarkInSessionStorage(markData, tagText);
              }
            }
          )
    }
  } else {
    removeTagFromMarkInSessionStorage(currentMarkSessionState, text);
    // hide tag
    tagElement.classList.add('hide');
    // hide 'delete all' when necessary
    const deleteAllTag
        = Array
        .from(assignedTagsElem.childNodes)
        .find(tag => tag.dataset.tagType === 'delete');
    if (deleteAllTag && assignedTagsElem.childNodes.length <= 6) {
      deleteAllTag.classList.add('hide');
    }
  }

  // save removal
  const { success } = await saveTags(markId);
  if (success) {
    if (deletingAll) {
      if (assignedTagsElem) {
        // remove the tag element from the existing tags area
        assignedTagsElem
            .childNodes
            .forEach(
              function(tagElement) {
                 tagElement = assignedTagsElem.childNodes[0];
                 tagElement.remove();
              }
            )
      }
    } else {
      tagElement.remove();

      const assignedTagsElem = findExistingTagsDivForMark(markId);
      const markObject = findMarkInSession(markId);
      manageDeleteAllButton(assignedTagsElem, markObject);
    }

    const tagManagementDiv = findTagManagementDivForMark(markId);
    const sortElement = tagManagementDiv.querySelector(`.sorted[data-sort-by]`);
    let {dataset: {sortBy}} = sortElement;
    updateTagManagementElements(markId, sortBy,'asc');
  } else {
    let msg = 'There was an error removing this tag.';
    if (deletingAll) {
      msg = 'There was an error removing all the tags on this annotation.';
      updateMarkInSectionCollectionInSessionStorage(allMarks, currentMarkSessionState);

      if (assignedTagsElem) {
        // un-hide the tag elements from the existing tags area
        assignedTagsElem
            .childNodes
            .forEach(
              function(tagElement) {
                tagElement.classList.remove('hide');
              }
            )
      }
    }

    const notice = new AsyncNotice(msg,'warning');
    if (notice) {
      await notice.create({}, true);
      notice.remove();
    }
  }

  if(tagsManagementElement) {
    removeOverlayOnElement(overlayId);
  }
}

/**
 * Reset tags edit mode; close the edit tags div and display the options div.
 * Ensure session variables are reset.
 *
 * @param controlsForTagsElement
 */
function cancelTagEdit(controlsForTagsElement) {
  const { dataset: { id: markId } } = controlsForTagsElement;
  const selector = `[data-controls-for="annotation"][data-id="${markId}"]`;
  const annotationControls = document.querySelector(selector);

  // reset the new tag controls
  const markElem = getMarkElement(markId);
  cancelNewTagCreation(markElem);

  controlsForTagsElement.classList.add('hide');
  if (annotationControls) {
    annotationControls.classList.remove('hide');
  }

  // ensure spinner spinner is removed
  const mark = getMarkElement(markId);
  const elementWithSpinner = mark.querySelector('.save-tag');
  if (elementWithSpinner) {
    elementWithSpinner.classList.remove('loading-on-top');
    elementWithSpinner.querySelector('.loader')?.remove();
  }
}

/**
 * Clear tag text input and hide new tag control.
 *
 * @param markElem
 */
function cancelNewTagCreation(markElem) {
  // clear and hide new tag control
  const newTagElem = getNewTagDiv(markElem);
  newTagElem.querySelector('span').innerText = '';
  newTagElem.classList.add('hide');

  // reset new tag control color
  updateColorOnElement(newTagElem, 'default');

  // clear text input
  document.querySelector('.add-edit-tag-area input').value = '';

  // reset suggested tags
  let { dataset: { markId = null, id = null } } = markElem;
  markId = markId ?? id;
  resetSuggestedTags(markId);

  // session storage management
  sessionStorage.removeItem('current-mark-new-tag-text-count');
  sessionStorage.removeItem('filtered-tags');
}

/**
 * returns the color keys or words used to represent the tag color options.
 *
 * @returns {string[]}
 */
function colorWords() {
  return ['default'].concat(Object.keys(appColors()));
}

/**
 * Find color class in an element's classList. If not found, returns false.
 *
 * @param classList
 * @returns {*|boolean}
 */
function findColorClassInClassList(classList) {
  for (let [_index, aClass] of classList.entries()) {
    if (colorWords().includes(aClass)) {
      return aClass;
    }
  }

  return false;
}

/**
 * Swap out the color edit controls with event handlers targeting the correct element
 * (new tag , or existing tag).
 *
 */
function changeTagColorControls(markId) {
  const tagColors = document.querySelector('[data-color-popup]');

  // update color controls - swap color controls back to new tag element
  const newTagColors = createColorControls(markId);

  // Change color controls to modify the selected tag
  tagColors.replaceWith(newTagColors);

  tagColors.classList.add('hide');
}

/**
 * Renders the controls to change the selected color for a tag.
 * The target can be either the new tag element or an existing tag element.
 * @param markId
 * @returns {HTMLDivElement}
 */
function createColorControls(markId) {
  const wrapper = document.createElement('div');
  wrapper.classList.add('color-selector', 'hide');
  wrapper.setAttribute('data-color-popup', '');

  // 1st element - section label
  const p = document.createElement('p');
  p.classList.add('directive');
  p.setAttribute('data-tag-colors-label', '');
  p.textContent = 'Select tag color:';

  wrapper.appendChild(p);
  // 2nd element - color controls
  const newTagColors = document.createElement('div');
  newTagColors.classList.add('tag-colors-wrapper');
  newTagColors.setAttribute('data-mark-id', markId);

  //TODO: fetch colors from site variables
  for (let color of colorWords()) {
    let colorChangeElem = document.createElement('div');
    if (color === 'default') {
      colorChangeElem.classList.add('selected');
    }
    colorChangeElem.classList.add('tag');
    colorChangeElem = updateColorOnElement(colorChangeElem, color);

    // event handler - change tag color - if we just edited an existing tag,
    // target element should be changed back
    colorChangeElem.addEventListener('pointerup', async (event) => {
      event.preventDefault();

      const tagManagementDiv = findTagManagementDivForMark(markId);
      const newTag = document.querySelector('.new-tag .tag');

      // const { dataset: { tagColor } } = event.target;
      // const wrapper = document.querySelector('.tag-colors-wrapper');

      changeColorHandler(event);
      // wrapper.querySelector(`[data-tag-color=${tagColor}]`);

      // const mark = event.target.closest('mark');
        await addNewTagHandler(event, tagManagementDiv, 'new');
      // }
    });

    newTagColors.appendChild(colorChangeElem);
  }

  // new tag color selector controls
  wrapper.appendChild(newTagColors);

 return  wrapper;
}

/**
 * Named handler for changing color of a target element.
 * Changes the color of the target element based on the event target's set color.
 * Also sorts suggestions by the given color
 *
 * @param event
 */
function changeColorHandler(event) {
  event.preventDefault();

  const { target } = event;
  const wrapper = target.closest('[data-controls-for="tags"]');
  const { dataset: { id: markId } } = wrapper;
  const markElem = target.closest('mark');
  const newTagDiv = markElem.querySelector(_newTagElementSelector(markId));
  const tagColor = detectNewColor(event, newTagDiv);

  if(newTagDiv) {
    changeNewTagDivColor(tagColor, newTagDiv);
  }

  let tagText = newTagDiv.innerText.trim();

  // update tag in session
  const tagData = { tagText, tagColor };
  const { dataset: { tagType } } = newTagDiv;

  const markData = findMarkInSession(markId);
  if(tagType === 'existing') {
    changeTagColorInMark(markData, tagData);
    updateTagInOptionsDiv(event, markId);
  }

  const allMarks = getSectionMarksFromSessionStorage();
  updateMarkInSectionCollectionInSessionStorage(allMarks, markData);

  let { sortBy, direction } = currentSuggestedTagsSortProperties(markId);
  updateTagManagementElements(markId, sortBy, direction);
}

/**
 * Tag color can either be selected by clicking on a color selector or with the arrow keys then pressing
 * 'Enter'. When selecting the color by clicking, we grab the color from the event target
 * @param event
 * @param newTagDiv
 * @returns {string}
 */
function detectNewColor(event = null, newTagDiv) {
  let color = 'default';

  // attempt to fetch new color from event target (color selector div clicked)
  if(event) {
    const { target: { classList, tagName, dataset: { tagColor } } } = event;

    if(tagName === 'DIV' && classList.contains('tag') && tagColor) {
      color = tagColor;
    }
  } else {
    // get the color of the new tag element
    let { dataset: { tagColor } } = newTagDiv;
    color = tagColor;
  }

  return color;
}

/**
 * Update a tag div element's color - both the class and data attribute.
 * If no previousColor is given, it checks the classList to see if a color exists.
 *
 * @param element
 * @param newColor
 * @param previousColor
 */
function updateColorOnElement(element, newColor, previousColor = null) {
  // ensure there is no color class in the classList
  if (!previousColor) {
    for(let color of colorWords()) {
      if (element.classList.contains(color)) {
        previousColor = color;
        break;
      }
    }
  }

  element.dataset.tagColor = newColor;

  if (previousColor) {
    element.classList.replace(previousColor, newColor);
  } else if (newColor) {
    element.classList.add(newColor);
  }

  return element;
}

/**
 * Save updated tag data for mark to the database.
 *
 * @param markId
 * @returns {Promise<void>}
 */
async function saveTags(markId) {
  let spinner
    = new Spinner(
    'save-tag',
    '[data-controls-for="tags"] .close-button',
    'afterend',
    false);

  spinner.add();

  let mark = findMarkInSession(markId);
  const tagsOnly = true;

  const u = new UserMarks();
  u.initialize(tagsOnly);

  const result = await u.saveMarks([mark]);

  if (result.success) {
    // update user tags list
    if (result.tags) {
      sessionStorage.setItem('userTags', JSON.stringify(result.tags));
    }

    // update marks collection
    let allMarks = getSectionMarksFromSessionStorage();

    // update tags on optionsDivs
    let markElem, optionsWrapper;
    for (let mark of result.marks) {
      updateMarkInSectionCollectionInSessionStorage(allMarks, mark);
      markElem = getMarkElement(mark.id);
      optionsWrapper = markElem.querySelector('[data-controls-for="annotation"] .other-options-wrapper');
      if (optionsWrapper) {
        renderTagsOnOptionsDiv(mark, optionsWrapper);
      }
    }
  } else {
    const notice = new AsyncNotice('There was an error saving your tags.', 'error');
    await notice.create({}, true);
    notice.remove();
  }

  spinner.remove();

  return result;
}

/**
 * Handler to add a new  tag. Created the tag div and adds it to the DOM.
 * Clears the 'filtered-tags' session storage variable.
 *
 * @param event
 * @param tagManagementDiv
 * @param tagType
 */
async function addNewTagHandler(event, tagManagementDiv, tagType) {
  event.preventDefault();

  const mark = tagManagementDiv.closest('mark');
  const existingTags = mark.querySelectorAll('.existing-tags .tag');
  const newTag = document.querySelector(`[data-tag-id="${mark?.dataset?.id}"][data-tag-type="new"]`);
  const { dataset: { tagText = null } } = newTag;
  const alreadyAdded = Array.from(existingTags).find((tag) => {
    return tag.dataset.tagText === tagText;
  }) ?? false;

  // exit if tag is already added
  if(alreadyAdded) {
    return;
  }

  // TODO: Update to manage diff bet. reader / annotations page requests (user userMarkRecordId)
  // tag details - handle requests from the reader or the annotations page
  let markId = null, userMarkRecordId;
  if(mark) {
    const {dataset: {id}} = mark;
    markId = id;
  } else {
    const userHighlight = event.target.closest('.user-highlight');
    if (userHighlight) {
      const {dataset: {markId: id, userMarkRecordId: markRecordId}} = userHighlight;
      markId = id;
      userMarkRecordId = markRecordId;
    }
  }
 const tagManagementElem = findTagManagementDivForMark(markId);
  if(tagManagementElem) {
    insertOverlayOnElement(tagManagementElem, `overlay-${markId}`);
  }

  let targetElement;
  if (tagType === 'new') {
    const markElem = findMarkElementFor(markId);
    targetElement = getNewTagDiv(markElem);
    targetElement.removeAttribute('data-current-color');
  } else {
    targetElement = event.target;

    if(!userMarkRecordId && targetElement.nodeName === 'SPAN') {
      targetElement = event.target.parentElement;
    }
  }


  // tag object
  const {
    dataset: {
      tagColor: color,
      tagId: id,
      tagLastUpdated: lastUpdated,
      tagUsage: usage,
      tagText: text
    }
  } = targetElement;

  let tag = { id, text, color, lastUpdated, usage};

  const alreadyAddedTag = findTagInExistingTags(markId, text, color);
  if (alreadyAddedTag) {
    // spotlight tag in assigned tags area
    spotlightTag(alreadyAddedTag);

    const tagManagementDiv = findTagManagementDivForMark(markId);
    const sortElement = tagManagementDiv.querySelector(`.sorted[data-sort-by]`);
    let {dataset: {sortBy}} = sortElement;
    updateTagManagementElements(markId, sortBy);
  } else {
    removeSpotlightFromTags();

    /** add tag to UI **/
    let tagElement, i;

    // button to remove
    i = document.createElement('i');
    i.classList.add('fa-solid', 'fa-xmark');

    tagElement = document.createElement('div');
    tagElement.classList.add('tag', color);
    tagElement.setAttribute('data-tag-type', 'existing');
    tagElement.setAttribute('data-tag-text', text);
    tagElement.setAttribute('data-mark-id', id);
    tagElement.setAttribute('title', `Usage: ${usage}`);
    tagElement = updateColorOnElement(tagElement, color);

    const tagSpan = document.createElement('span');
    tagSpan.setAttribute('data-tag-text', text);
    tagSpan.setAttribute('data-tag-color', color);
    tagSpan.textContent = text;
    tagElement.appendChild(tagSpan);
    tagElement.appendChild(i);

    // delete handler - editing an existing tag
    tagElement.addEventListener(
      'click',
        async (event) => {
          await removeTagHandler(event, markId);
      }
    );

    // add to tags div
    const assignedTagsElem = findExistingTagsDivForMark(markId);
    const deleteButton = assignedTagsElem.querySelector('[data-tag-type="delete"]');

    if (deleteButton) {
      deleteButton.insertAdjacentElement('beforebegin', tagElement);
    } else {
      // loop through existing tags, insert tag at sorted position
      const currentTags = assignedTagsElem.querySelectorAll('.tag');
      insertAlphabetically(currentTags, tagElement);
    }

    const markObject = findMarkInSession(markId);
    manageDeleteAllButton(assignedTagsElem, markObject);

    // find mark in session storage
    let marks = JSON.parse(sessionStorage.getItem('marks'));
    let markData = findMarkInSectionCollectionInSessionStorage(marks, markId);
    const markDataCopy = JSON.stringify(markData);

    // add tag to mark details
    if (markData.tags) {
      markData.tags.push(tag);
    } else {
      markData.tags = [tag];
    }

    // update mark in session
    updateMarkInSectionCollectionInSessionStorage(marks, markData);

    const { success } = await saveTags(markId);
    if (success) {
      // update session variables
      initializeTagSessionStorageVariables(markId);

      // change back the color controls to focus on the new tag element
      changeTagColorControls(markId);

      // clear color on new tag element
      let newTagColor = findColorClassInClassList(targetElement.classList);
      updateColorOnElement(targetElement, newTagColor, 'default');
      const markElem = getMarkElement(markId);
      cancelNewTagCreation(markElem);

      const tagManagementDiv = findTagManagementDivForMark(markId);
      const sortElement = tagManagementDiv.querySelector(`.sorted[data-sort-by]`);
      let { dataset: { sortBy } } = sortElement;
      updateTagManagementElements(markId, sortBy);
    } else {
      const msg = 'There was a problem adding this tag.';
      const notice = new AsyncNotice(msg, 'warning');
      await notice.create({}, true);

      // TODO: undo update to mark in session
      updateMarkInSectionCollectionInSessionStorage(marks, markDataCopy);

      notice.remove();
    }
  }

  if(tagManagementElem) {
    const textInput = tagManagementElem.querySelector('[data-id="tag-input"]');
    textInput?.focus();

    removeOverlayOnElement(`overlay-${markId}`);
  }
}

/**
 * Update tag color in mark tag.
 *
 * @param markData
 * @param tag
 */
function changeTagColorInMark(markData, tag) {
  if (markData.tags) {
    for (let markTag of markData.tags) {
      if (markTag.text === tag.text) {
        markTag.color = tag.color;
      }
    }
  }
}

/**
 * Update the color on the tag in the options div.
 *
 * @param event
 * @param tag
 */
function updateTagInOptionsDiv(event, tag) {
  const markElem = event.target.closest('mark');
  if (markElem) {
    const tagsWrapper = markElem.querySelector('[data-controls-for="annotation"] .tags-wrapper');

    if (tagsWrapper) {
      for (let tagElem of tagsWrapper.querySelectorAll('.tag')) {
        if (tagElem.innerText.trim() === tag.text) {
          updateColorOnElement(tagElem, tag.color);
        }
      }
    }
  }
}

/**
 * Render tags on the options div or the manage tags div.
 * The 'target' can be either 'tags', 'annotation'.
 *
 * @param markData
 * @param target
 * @param showUsage
 * @returns {HTMLDivElement}
 */
function renderExistingTags(markData, target, showUsage = false) {
  const tagsDiv = document.createElement('div');
  const marksCount = markData?.tags?.length ? markData.tags.length : 0;
  const markId = markData.id;

  tagsDiv.classList.add('existing-tags');

  // if mark has tags
  if (marksCount > 0) {
    // loop through tags on mark - create tag elements
    // render existing tags
    const sorted = markData.tags.sort(tagsSortingFunction('name', 'asc'));
    for (let [index, tagObject] of sorted.entries()) {
      /* Sample tag: <div className="tag default" data-id="1' data-type='existing' data-tag-color='red>
                       <span>divinity</span>
                       <i className="fa-solid fa-xmark"></i>
                     </div>
       */
      let i;
      const text = tagObject.text === undefined ? '!' : tagObject.text;
      const color = tagObject.color === undefined ? '!' : tagObject.color;
      const usage = tagObject.usage === undefined ? '!' : tagObject.usage;

      if (target === 'tags') {
        i = document.createElement('i');
        i.classList.add('fa-solid', 'fa-xmark');
        i.setAttribute('data-id', index.toString());
      }

      // tag div
      let tagElement = document.createElement('div');
      tagElement.classList.add('tag', 'default');
      tagElement.setAttribute('data-tag-id', tagObject.id);
      tagElement.setAttribute('data-tag-type', 'existing');
      tagElement.setAttribute('data-tag-text', text);
      tagElement.setAttribute('title', `Usage: ${usage}`);
      tagElement = updateColorOnElement(tagElement, color);

      const span = document.createElement('span');
      span.setAttribute('data-tag-text', text);
      span.setAttribute('data-tag-color', color);
      span.textContent = text;

      //TODO: @carolynl, somehow the class 'show-for-sr' is being added automatically, but that class is making
      //      the span content invisible...
      span.classList.remove('show-for-sr');

      tagElement.appendChild(span);

      if(showUsage) {
        const usageSpan = document.createElement('span');
        usageSpan.classList.add('tag-usage');
        usageSpan.innerHTML = usage;
      }

      if (target === 'tags') {
        tagElement.appendChild(i);
      }

      // event handler
      tagElement.addEventListener('click', async (event) => {
        tagObject.markId = markId;
        await removeTagHandler(event, markId);
      })

      tagsDiv.appendChild(tagElement);
    }
  }

  return tagsDiv;
}

/**
 * create a tag element in the DOM.
 *
 * @param markId
 * @param tagData
 * @param tagType
 * @param showUsage
 * @returns {any}
 */
function createNewTagDiv(markId, tagType, tagData, showUsage = false) {
  let newTag = document.createElement('div');
  let color, newTagDiv, text;

  newTag.id = '';
  newTag.classList.add('tag');
  newTag.setAttribute('data-tag-type', tagType);

  if (tagType === 'delete') {
    newTag.setAttribute('title', 'Delete all tags');
    newTag.classList.add('delete');
    text = 'Delete All';
  } else {
    newTag.setAttribute('data-tag-color', tagData.color);
    newTag.setAttribute('data-tag-id', tagData.id);
    newTag.setAttribute('data-tag-last-updated', tagData.lastUpdated);
    newTag.setAttribute('data-tag-text', tagData.text);
    newTag.setAttribute('data-tag-usage', tagData.usage);
    newTag.setAttribute('title', `Usage: ${tagData.usage}`);
    newTag.setAttribute('data-tag-id', markId);
    newTag.dataset.markId = markId;

    if (tagType === 'new') {
      if (newTagDiv) {
        newTagDiv = findMarkElementFor(markId);
        color = newTagDiv.dataset.color;
        text = newTagDiv.textContent;
      } else {
        color = "default";
        text = "";
      }
      newTag.classList.add('hide');
    } else {
      color = tagData.color;
      text = tagData.text;
    }
  }

  const newTagSpan = document.createElement('span');
  newTagSpan.setAttribute('data-tag-text', text) ;
  if (tagType !== 'delete') {
    newTagSpan.setAttribute('data-tag-color', color);
  }
  newTagSpan.textContent = text;

  newTag = updateColorOnElement(newTag, color);

  // click event handler
  newTag.addEventListener('click', async (event) => {
    event.preventDefault();

    // disable all tags so no other action occurs until this click is handled properly
    if (tagType === 'delete') {
      // warning to user
      console.log('are you sure notice');

      // delete all tags
      console.log('delete all tags');

      // guard clause
      let deleteAll = confirm('Are you sure you want to delete all the tags on this annotation?');
      if (deleteAll) {
        await removeTagHandler(event, markId);
      } else {
        console.log("cancel 'delete all'");
      }
    } else if (tagType === 'suggested') {
      const tagManagementDiv = findTagManagementDivForMark(markId);
      changeColorHandler(event);
      await addNewTagHandler(event, tagManagementDiv, tagType);
    } else {
      // show the color selector & set focus on the wrapper
      const markElem = getMarkElement(markId);
      const colorSelector = markElem.querySelector('[data-color-popup]');
      const colorSelectorWrapper = colorSelector.querySelector('.tag-colors-wrapper');

      colorSelector.classList.remove('hide');
      if (!colorSelectorWrapper.hasAttribute('tabindex')) {
        colorSelectorWrapper.setAttribute('tabindex', '0');
      }
      colorSelectorWrapper.focus();
    }
  })

  const i = document.createElement('i');
  // Add text span
  newTag.appendChild(newTagSpan);
  if (tagType === 'delete') {
    i.classList.add('fa-solid', 'fa-trash-alt');
    newTag.appendChild(i);
  }

  if(showUsage) {
    const usageSpan = document.createElement('span');
    usageSpan.classList.add('tag-usage');
    usageSpan.innerHTML = tagData.usage;

    newTag.appendChild(usageSpan);
  }

  if (tagType === 'new') {
    i.classList.add('fa-solid', 'fa-plus');
    newTag.appendChild(i);
  }

  return newTag;
}

/**
 * Remove all the childNodes of a parent element.
 *
 * @param parent
 */
function removeAllChildNodes(parent) {
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }
}

/**
 * Remove tags from suggested tags div.
 * This is used when a new tag has been added and the suggestions area needs to be cleared.
 *
 * @param markId
 */
function resetSuggestedTags(markId) {
  const markElem = getMarkElement(markId);
  const wrapper = markElem.querySelector('.suggested-tags');

  while (wrapper.firstChild) {
    wrapper.removeChild(wrapper.firstChild);
  }

  const noneSpan= document.createElement('span');
  noneSpan.innerText = 'No suggestions.';
  wrapper.appendChild(noneSpan);
}

/**
 * JS version of PHP array_column() method.
 *
 * @param input
 * @param columnKey
 * @returns {*}
 */
function arrayColumn (input, columnKey) {
  // guard clause
  if (input === null || input === undefined) {
    return '';
  }

  return input.map(function(value, _index) {
    return value[columnKey];
  })
}

/**
 * Return new tag div for the mark in tag edit mode.
 * @param markElem
 * @returns {Element}
 */
function getNewTagDiv(markElem) {
  const { dataset: { markId = null, id = null } } = markElem;
  const markElemId = markId ?? id;
  return markElem.querySelector(`.new-tag .tag[data-mark-id="${markElemId}"`);
}

/**
 * Generate the tags management div with updated suggested tags.
 *
 * @param markId
 * @param sortBy - one of: ('name', 'time', 'usage'); default: 'name'
 * @param direction - one of: ('asc', 'desc'); default: 'asc'
 * @param userMarkRecordId
 */
function updateTagManagementElements(
    markId,
    sortBy = 'name',
    direction = 'asc',
    userMarkRecordId = null) {
  const CURRENT_MARK_NEW_TAG_TEXT_COUNT_KEY = 'current-mark-new-tag-text-count';
  const FILTERED_TAGS_LIST_KEY = 'filtered-tags';
  const USER_TAGS_LIST_KEY = 'userTags';
  const allTags = JSON.parse(sessionStorage.getItem(USER_TAGS_LIST_KEY));

  const markElem = findMarkElementFor(markId, userMarkRecordId);
  const textInputElem = markElem.querySelector('[data-controls-for="tags"] [data-id="tag-input"]');
  const newTagColorsDiv = markElem.querySelector('[data-color-popup]');
  const inputText = textInputElem.value;


  let markData = null;
  let markTags = [];
  if (userMarkRecordId) {
    const selector = `.card-view[data-mark-id="${markId}"][data-user-mark-record-id="${userMarkRecordId}"]`;
    const cardView = document.querySelector(selector);
    if (cardView) {
      const markTagsCollection = cardView.querySelectorAll('.tags-wrapper .tag');
      if (markTagsCollection){
        markTagsCollection.forEach((tagElement) => {
          let { dataset: { id, color, text, usage, lastUpdated } } = tagElement;
          markTags.push({id, color, text, usage, lastUpdated });
        });
      }
    }
  } else {
    markData = findMarkInSession(markId);
    markTags = markData.tags ?? [];
  }


  const selectedColor = currentSelectedColor(markId);

  // hide the tag colors management
  if (!newTagColorsDiv.classList.contains('hide')) {
    newTagColorsDiv.classList.add('hide');
  }

  /* manage tags management variables */
  // filter() function to remove tags already added to mark
  const filterAlreadyAddedTags = (tag) => !markTags.some((markTag) => markTag.text === tag.text);

  // filter() function to remove tags without selected color
  // const filterSelectedColorTags = (tag) =>  tag.color === selectedColor;

  let filteredTags
    = allTags
    .filter(filterAlreadyAddedTags)
    .sort(tagsSortingFunction(sortBy,direction));

  // put back trimmed text so we don't have spaces
  textInputElem.value = inputText;

  // grab previous text input value, save current
  const previousTextInputValue = sessionStorage.getItem(CURRENT_MARK_NEW_TAG_TEXT_COUNT_KEY);
  sessionStorage.setItem(CURRENT_MARK_NEW_TAG_TEXT_COUNT_KEY, inputText.length);

  // hide the colors palette when there's no text in the input field
  if (textInputElem?.value.length === 0) {
    if (!newTagColorsDiv.classList.contains('hide')) {
      newTagColorsDiv.classList.add('hide');
    }
    textInputElem.focus();
  }

  // For performance reasons, we keep and use a filtered tags list instead of always rechecking the full
  // tags list. Decide when we need to reset the filtered tags list.
  let resetTagsList
    = ((filteredTags.length ?? 0) === 0) // if the filtered Tag List is empty
    || (textInputElem.value.length === 0) // if there are no characters in the new tag input text field
    || (previousTextInputValue > textInputElem.value.length); // if the user removed a character

  if (resetTagsList) {
    // copy by value, not reference
    filteredTags = [...allTags].sort(tagsSortingFunction(sortBy, direction));
  }

  // list of all visible tag names:
  // (1) from filtered tags list
  // (2) existing tags
  // (3) newly added tags that have not been saved.
  const all = filteredTags.concat(markTags);
  const visibleTagNamesMap = new Map();
  all.forEach((item) => {
    visibleTagNamesMap.set(item.text, {id: item.id, color: item.color});
  });

  // manage suggested tags
  let tagDiv;
  const suggestedTagsDiv = markElem.querySelector('[data-controls-for="tags"] .suggested-tags');

  // suggestions div
  let newSuggestedTagsDiv = suggestedTagsDiv.cloneNode();

  let tagTexts = arrayColumn(markTags, 'text');
  if ((filteredTags?.length ?? 0) > 0) {
    // clear the filtered tags list and fill with remaining possible matches, below
    let temp = [];

    for (let tagData of filteredTags) {
      let {text} = tagData;

      if (text.startsWith(textInputElem.value) && !tagTexts.includes(text)) {
        temp.push(tagData);
        // create suggested tag with tag data
        const showUsage = true;
        tagDiv = createNewTagDiv(markId, 'suggested', tagData, showUsage);
        tagDiv.setAttribute('title', `Usage: ${tagData.usage ?? 0}`);
        newSuggestedTagsDiv.appendChild(tagDiv);

        const deleteTagElem = tagDiv.querySelector('[data-tag-type="delete"]');
        if (deleteTagElem) {
          deleteTagElem.remove();
          tagDiv.appendChild(newSuggestedTagsDiv);
        }
      }
    }
    sessionStorage.setItem(FILTERED_TAGS_LIST_KEY, JSON.stringify([...temp]));
  }

  if (newSuggestedTagsDiv.childNodes.length > 0) {
    suggestedTagsDiv.replaceChildren();
    suggestedTagsDiv.replaceWith(newSuggestedTagsDiv);
  } else {
    removeAllChildNodes(suggestedTagsDiv);

    const noneSpan = document.createElement('span');
    noneSpan.innerText = 'No suggestions.';
    suggestedTagsDiv.appendChild(noneSpan);
  }

  if (textInputElem) {
    const newTag = getNewTagDiv(markElem);
    const newTagWrapper = newTag.parentElement;
    const noticeElement = newTagWrapper.querySelector('.tm-notice');
    const alreadyAddedTag = findTagInExistingTags(markId, inputText);
    const existingTag = findTagOnPageByText(inputText);

    let newTagSpan = newTagWrapper.querySelector('span');
    newTagSpan.setAttribute('data-tag-text', inputText);
    newTag.setAttribute('data-tag-text', inputText)
    newTagSpan.textContent = inputText;

    // manage new tag details
    let {dataset: {tagColor: newTagColor, currentColor: newTagCurrentColor = null}} = newTag;
    if (visibleTagNamesMap.has(inputText)) {
      const matchingTagData = visibleTagNamesMap.get(inputText);
      // set new tag color to match existing tag (set current color in dataset)
      newTag.setAttribute('data-current-color', newTagColor);
      newTag.setAttribute('data-tag-color', matchingTagData.color);
      newTag.setAttribute('data-tag-text', inputText);

      newTag.classList.replace(newTagColor, matchingTagData.color);

      const onReader = isTagManagementOnReader();
      let message = 'Tag must be uniquely named';
      const tagStatus = isTagAddedOrSuggested(existingTag);

      // handling an event on the reader
      if (onReader) {
        if (tagStatus === 'suggested') {
          message = 'Tag already exists';
        }

        // added
        else {
          message = 'Tag already assigned';
        }
      }

      spotlightTag(existingTag);
      disableNewTagDiv(markElem);
      setNewTagNotice(markElem, message);

      if (alreadyAddedTag) {
        spotlightTag(alreadyAddedTag);
      }
    } else {
      removeSpotlightFromTags();

      // control visibility of new tag div
      clearNewTagNotice(markId);
      if (newTag && textInputElem.value.length > 0) {
        // ensure new tag element has selected color
        if (selectedColor) {
          newTag.setAttribute('data-tag-color', selectedColor);
          const disabled = newTag.classList.contains('disabled') ? 'disabled' : '';

          newTag.className = '';
          newTag.classList.add('tag', selectedColor);
          if(disabled) {
            newTag.classList.add(disabled);
          }
        }
        enableNewTagDiv(markElem);
      } else {
        disableNewTagDiv(markElem);
        clearNewTagNotice(markId);
      }
    }

    // update tag name with contents of input text field
    if (inputText) {
      newTag.setAttribute('data-tag-text', inputText);
      newTagSpan.textContent = inputText;
    }
  }
}

/**
 * Fin the user highlight element on Account Annotations page with the given markId and userMarkRecordId.
 *
 * @param markId
 * @param userMarkRecordId
 * @returns {Element}
 */
function getUserHighlight(markId, userMarkRecordId) {
  return document.querySelector(`[data-mark-id="${markId}"][data-user-mark-record-id="${userMarkRecordId}"]`);
}

/**
 * Initialize session storage variables related to tags.
 */
function initializeTagSessionStorageVariables(markId) {
  const marks = getSectionMarksFromSessionStorage();
  const mark = findMarkInSectionCollectionInSessionStorage(marks, markId);
  const allTagsJson = sessionStorage.getItem('userTags');
  const allTags = JSON.parse(allTagsJson);
  const markTags = mark?.tags ?? [];

  let filteredTags
    = allTags
      .filter((tag) => !markTags.includes(tag))
      .sort(sortTagsByNameSortingFunction);

  // initialize session storage variables
  sessionStorage.setItem('filtered-tags', JSON.stringify(filteredTags));
}

/**
 * Add tags to the options div.
 *
 * @param markObject
 * @param optionsWrapper
 */
function renderTagsOnOptionsDiv(markObject, optionsWrapper) {
  const markElem = getMarkElement(markObject.id);
  const wrapperClass = 'tags-wrapper';
  let tagsWrapper;

  // if a markElement exists for the current mark, remove any existing tags before re-rendering
  if (markElem) {
    tagsWrapper = markElem.querySelector(`.${wrapperClass}`);
    if (tagsWrapper) {
      tagsWrapper.remove();
    }
  }

  // add mark tags if tags present
  if (markObject.tags?.length > 0) {
    let tagElem;
    tagsWrapper = document.createElement('div');
    tagsWrapper.classList.add(wrapperClass,'reader');

    // sort and loop through tags on mark - create tag elements
    if (markObject.tags) {
      let tagsList = markObject.tags.sort(tagsSortingFunction('name', 'asc'));
      for (let [_index, tag] of tagsList.entries()) {
        tagElem = document.createElement('div');
        tagElem.classList.add('tag', `${tag.color}`);
        const textSpan = document.createElement('span');
        textSpan.innerText = tag.text;
        tagElem.appendChild(textSpan);

        tagsWrapper.appendChild(tagElem);
      }
    }

    // if present, replace
    const currentTagsWrapper = markElem.querySelector('.tags-wrapper');
    if (currentTagsWrapper) {
      currentTagsWrapper.replaceWith(tagsWrapper);
    } else {
      // else, insert
      optionsWrapper.insertAdjacentElement('afterend', tagsWrapper);
    }
  }
}

/**
 * Returns the mark element in the dom with the given id.
 *
 * @param markId
 * @returns {Element}
 */
function getMarkElement(markId) {
  return document.querySelector(`mark[data-id="${markId}"]`);
}

/**
 * Element selector for new tag div element.
 *
 * @returns {string}
 * @private
 */
function _newTagElementSelector(markId) {
  return `.tag[data-tag-type="new"][data-mark-id="${markId}"]`;
}

/** Add or remove the 'delete all' button from the assigned tags area
 *
 * @param assignedTagsDiv
 * @param markData
 */
function manageDeleteAllButton(assignedTagsDiv, markData) {
  const deleteTagElem = assignedTagsDiv.querySelector('[data-tag-type="delete"]');
  const currentTags = assignedTagsDiv.querySelectorAll(`.tag:not([data-tag-type="delete"])`);

  // guard clause
  if (!assignedTagsDiv) {
    return;
  }

  // we show the delete all button if there are more than 4 tags on the mark
  if (currentTags.length  > 4) {
    if (deleteTagElem) {
      return;
    }

    createDeleteAllButton(assignedTagsDiv, markData);
  } else {
    if (deleteTagElem) {
      deleteTagElem.remove();
    }
  }
}

/**
 * Create the delete all tags button.
 *
 * @param assignedTagsDiv
 * @param markData
 */
function createDeleteAllButton(assignedTagsDiv, markData) {
  const deleteTagElem = createNewTagDiv(markData.id, 'delete', {text: 'Delete All'});
  assignedTagsDiv.appendChild(deleteTagElem);
}

/**
 * Return the options div for a given mark in DOM by markId
 *
 * @param markId
 * @returns {Element|null}
 */
function findOptionsDivForMark(markId) {
  const div = document.querySelector(`[data-id="${markId}"] [data-controls-for="annotation"]`);
  if (div) {
    return div;
  }

  return null;
}

/**
 * Return the tags management div in DOM by markId
 *
 * @param markId
 * @returns {Element|null}
 */
function findTagManagementDivForMark(markId) {
  const div = document.querySelector(`[data-id="${markId}"][data-controls-for="tags"]`);

  if (div) {
    return div;
  }

  return null;
}

/** Return the existing tags div for the given mark.
 *
 * @param markId
 * @returns {Element|null}
 */
function findExistingTagsDivForMark(markId) {
  const div = findTagManagementDivForMark(markId);
  if (div) {
    return div.querySelector('.existing-tags');
  }

  return null;
}

/**
 * Return the tag element from existing tags, if found. Otherwise, return null.
 *
 * @param markId
 * @param text
 * @param color
 * @returns {HTMLElement|boolean}
 */
function findTagInExistingTags(markId, text, color=null) {
  const div = findExistingTagsDivForMark(markId);

  if (!div) {
    return false;
  }

  let selector = `.tag span[data-tag-text="${text}"]`;
  if (color) {
    selector =  `${selector}[data-tag-color="${color}"]`;
  }

  const elem = div.querySelector(selector);
  if (elem) {
    return elem.parentElement;
  }

  return false;
}

/**
 * Find a specific tag in assign Tags div by color and text.
 *
 * @param assignedTagsElem
 * @param tagData
 *
 * @returns {Element|null}
 */
function findTagInAssignedTagsDivForMark(assignedTagsElem, tagData) {
  // return null if div not found
  if (assignedTagsElem === null) {
    return null;
  }

  let { color, text } = tagData;
  const span = assignedTagsElem.querySelector(`[data-tag-text="${text}"][data-tag-color="${color}"]`);
  if (span === null) {
    return null;
  }

  return span.closest('.tag');
}

/**
 * Toggle the visibility of the mark management divs. Either show the annotation div or the options div, hide the other.
 *
 * @param markId
 * @param divToShow - one of: ('annotation', 'tags')
 */
function toggleVisibilityOfMarkManagementDivs(markId, divToShow) {
  const optionsDiv = findOptionsDivForMark(markId);
  const tagManagementDiv = findTagManagementDivForMark(markId);

  if (divToShow === 'annotation') {
    tagManagementDiv.classList.add('hide');
    optionsDiv.classList.remove('hide');

    return;
  }

  if (divToShow === 'tags') {
    optionsDiv.classList.add('hide');
    tagManagementDiv.classList.remove('hide');
  }
}

/**
 * Insert a tag in its correct alphabetical order.
 *
 * @param currentTags
 * @param tagElement
 */
function insertAlphabetically(currentTags, tagElement) {
  if (currentTags.length === 0) {
    const assignedTagsElem = findExistingTagsDivForMark(tagElement.dataset.markId);
    assignedTagsElem.appendChild(tagElement);

    return;
  }

  for(let currentTag of currentTags) {
    let sort = compare(currentTag.textContent, tagElement.textContent);
    if(sort > -1) {
      currentTag.insertAdjacentElement('beforebegin',tagElement);

     return;
    }
  }

  // if not inserted, insert at end
  const last = currentTags.item(currentTags.length - 1);
  last.insertAdjacentElement('afterend', tagElement);
}

/**
 * Removes the given item from array
 * @param array
 * @param value
 */
function removeFirstOccurrenceFromArray(array, value) {
  const index = array.indexOf(value);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
}

/**
 * Return the color currently selected in the tag color control. if none is selected, return null.
 *
 * @param markId
 * @returns {null|string}
 */
function currentSelectedColor(markId) {
  const tagsDiv = findTagManagementDivForMark(markId);
  const selectedColorElem = tagsDiv.querySelector('.tag-colors-wrapper .tag.selected');
  if(selectedColorElem) {
    return selectedColorElem.dataset.tagColor;
  }

  return null;
}

/**
 * Method to change the color of a new Tag div according to the color selector clicked or selected.
 *
 * @param newColor
 * @param newTagDiv
 */
function changeNewTagDivColor(newColor, newTagDiv) {
  const { dataset: { markId } } = newTagDiv;
  let colorsWrapper, selector;

  if(markId) {
    selector =  `.tag-colors-wrapper[data-mark-id="${markId}"]`;
  } else {
    selector = '.tag-colors-wrapper';
  }
  colorsWrapper = document.querySelector(selector);
  const selectedColorElem = colorsWrapper.querySelector('.selected');
  const { dataset: { tagColor } } = selectedColorElem;

  // previous, new color
  let previousColor = findColorClassInClassList(newTagDiv.classList);

  if (colorWords().includes(tagColor)) {
    let currentElem = colorsWrapper.querySelector('.selected');

    // handle click to set or unset a color selection
    if (currentElem) {
      currentElem.classList.remove('selected');
    }

    const newElem = colorsWrapper.querySelector(`[data-tag-color="${newColor}"]`);
    if(newElem) {
      newElem.classList.add('selected');

      newTagDiv.classList.remove(tagColor);

      // update color classes, data-attributes
      newTagDiv.classList.add(newColor);
      newTagDiv.setAttribute('data-tag-color', newColor);
      const span = newTagDiv.querySelector('span');
      span.setAttribute('data-tag-color', newColor);
    }

    // update new tag element with color
    updateColorOnElement(newTagDiv, newColor, previousColor);
  }
}

/**
 * Returns the sort properties for the suggested tags that are currently active on the DOM.
 * Finds the sort button that is selected and reads its properties.
 *
 * @param markId
 * @returns {{sortBy: string, direction: string}|{sortBy, direction: string}}
 */
function currentSuggestedTagsSortProperties(markId) {
  const tagsDiv = findTagManagementDivForMark(markId);
  const direction = 'asc'; // TODO: update once we add ability to change direction
  // return default sort values if div not present
  if(!tagsDiv) {
    return { sortBy: 'name', direction };
  }

  const sortElem = tagsDiv.querySelector('span.sorted[data-sort-by]');
  let { dataset: { sortBy } } = sortElem;

  return { sortBy, direction };
}

/**
 * Simple compare function for sort(), toSorted()
 * @param a
 * @param b
 * @returns {number}
 */
function compare(a, b) {
  if(a < b) {
    return -1;
  }

  if(a > b) {
    return 1;
  }

  return 0;
}

/**
 * Disable a mark's new tag div in the tags management pane.
 * @param markElem
 */
function disableNewTagDiv(markElem) {
  const newTag = getNewTagDiv(markElem);

  if(newTag) {
    newTag.classList.add('hide');
    newTag.setAttribute('disabled', 'true');

    if(!newTag?.classList?.contains('disabled')) {
      newTag.classList.add('disabled');
    }

    const plusLink = newTag.querySelector('.fa-plus');
    if(plusLink) {
      plusLink.classList.add('hide');
    }
  }
}

/**
 * Hide a mark's new tag div in the tag management pane.
 *
 * @param markElem
 */
function hideNewTagDiv(markElem) {
  const newTag = getNewTagDiv(markElem);
  if (newTag) {
    if (!newTag?.classList?.contains('hide')) {
      newTag.classList.add('hide');
    }
  }
}

/**
 * Enable a mark's new tag div in the tags management pane.
 * @param markElem
 */
function enableNewTagDiv(markElem) {
  const newTag = getNewTagDiv(markElem);

  newTag.removeAttribute('disabled');
  newTag.classList.remove('disabled');
  newTag.classList.remove('hide');

  const plusLink = newTag.querySelector('.fa-plus');
  if (plusLink) {
    plusLink.classList.remove('hide');
  }
}

/**
 * Clear the new tag notices element for a given mark.
 * Finds the tag management div by the mark id.
 *
 * @param markId
 */
function clearNewTagNotice(markId) {
  const tagsManagementElement = findTagManagementDivForMark(markId);
  const newTagNotice = tagsManagementElement.querySelector('.tm-notice');
  const plusLink = tagsManagementElement.querySelector('.fa-plus');

  if(newTagNotice) {
    if(!newTagNotice.classList.contains('hide')) {
      newTagNotice.classList.add('hide');
    }
    newTagNotice.textContent = '';
  }

  if(plusLink) {
    plusLink.classList.add('hide');
  }
}

/**
 * Set the new tag notice element for a given mark.
 * Finds the tag management div in the mark Element.
 *
 * @param markElem
 * @param text
 */
function setNewTagNotice(markElem, text) {
  const { dataset: { markId = null, id = null } } = markElem;
  const markElemId = markId ?? id;
  const tagsManagementElement = findTagManagementDivForMark(markElemId);
  const newTagWrapper = getNewTagDiv(markElem).parentElement;
  const newTagNoticeWrapper = newTagWrapper.querySelector('[data-notice]');
  const newTagNotice = newTagNoticeWrapper.querySelector('.tm-notice');
  const plusLink = tagsManagementElement.querySelector('.fa-plus');
  if(plusLink) {
    plusLink.classList.add('hide');
  }

  if(newTagNoticeWrapper && newTagNotice) {
    newTagNotice.textContent = text;

    if(newTagNoticeWrapper.classList.contains('hide')) {
      newTagNoticeWrapper.classList.remove('hide');
    }
  }
}

/**
 * Returns one of the following:
 * - 'added' if the tag has been assigned to the mark in question.
 * - 'suggested' if the tag has not been assigned to the mark in question.
 * - 'neither' if neither case is true (error case).
 *
 * @param tag
 * @returns {string}
 */
function isTagAddedOrSuggested(tag) {
  let response = 'neither';
  if (!tag) {
    return response;
  }

  const parent = tag.parentElement;
  if (!parent) {
    return response;
  }

  if (parent.classList.contains('existing-tags')) {
    response = 'added';
  }

  if (parent.classList.contains('suggested-tags')) {
    response = 'suggested';
  }

  return response;
}

/**
 * Find the mark Element on the page for a mark id. This function is used on the reader as well as on the
 * Annotations page. On the Annotations page, we the elements have a secondary data attribute - userMarkRecordId.
 * If a userMarkRecordId is provided, search for the mark element with that attribute. This is needed b/c
 * mark is not atomic on this, only on a section in the reader.
 * @param markId
 * @param userMarkRecordId
 * @returns {Element}
 */
function findMarkElementFor(markId, userMarkRecordId = null) {
  if (userMarkRecordId) {
    return getUserHighlight(markId, userMarkRecordId);
  }
  return getMarkElement(markId);
}

export {
  addNewTagHandler, arrayColumn, changeNewTagDivColor, createNewTagDiv, createTagManagementControls,
  updateTagManagementElements, cancelTagEdit, initializeTagSessionStorageVariables, findOptionsDivForMark,
  findTagManagementDivForMark, spotlightTag, removeSpotlightFromTags, removeAllChildNodes, renderTagsOnOptionsDiv,
  saveTags, tagsSortingFunction, isTagManagementOnReader}
